import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Plus, Edit, Trash2, Download } from "lucide-react";
import { exportToExcel } from "../lib/excel";
import { API } from "../lib/api";
import { DatePicker } from "../components/DatePicker";
import { useConfirm } from "../components/ConfirmDialog";

const Custos = () => {
  const [custos, setCustos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categorias, setCategorias] = useState([]);
  const [filtros, setFiltros] = useState({
    dataInicio: '',
    dataFim: '',
    categoria: 'todas'
  });
  const [formData, setFormData] = useState({
    data: '',
    categoria: '',
    descricao: '',
    valor: '',
    observacoes: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [confirm, confirmDialog] = useConfirm();

  const fetchCustos = async () => {
    try {
      const params = new URLSearchParams();
      if (filtros.dataInicio) params.append('data_inicio', filtros.dataInicio);
      if (filtros.dataFim) params.append('data_fim', filtros.dataFim);
      if (filtros.categoria && filtros.categoria !== 'todas') params.append('categoria', filtros.categoria);
      
      const response = await axios.get(`${API}/custos?${params.toString()}`);
      setCustos(response.data);
    } catch (error) {
      toast.error("Erro ao carregar custos");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategorias = async () => {
    try {
      const response = await axios.get(`${API}/categorias-custo`);
      setCategorias(response.data.categorias || []);
    } catch (error) {
      console.error("Erro ao carregar categorias");
    }
  };

  useEffect(() => {
    const carregar = async () => {
      await fetchCustos();
      await fetchCategorias();
    };
    carregar();
  }, [filtros]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.data) {
      toast.error("Data é obrigatória");
      return;
    }
    try {
      const custoData = {
        ...formData,
        valor: parseFloat(formData.valor)
      };

      if (editingId) {
        await axios.put(`${API}/custos/${editingId}`, custoData);
        toast.success("Custo atualizado com sucesso!");
      } else {
        await axios.post(`${API}/custos`, custoData);
        toast.success("Custo criado com sucesso!");
      }

      setFormData({
        data: '',
        categoria: '',
        descricao: '',
        valor: '',
        observacoes: ''
      });
      setEditingId(null);
      setIsDialogOpen(false);
      fetchCustos();
    } catch (error) {
      toast.error("Erro ao salvar custo");
    }
  };

  const handleEdit = (custo) => {
    setFormData({
      data: custo.data,
      categoria: custo.categoria,
      descricao: custo.descricao,
      valor: custo.valor.toString(),
      observacoes: custo.observacoes || ''
    });
    setEditingId(custo.id);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: "Excluir custo?",
      description: "Tem certeza que deseja deletar este custo? Esta ação não pode ser desfeita.",
      confirmText: "Excluir",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/custos/${id}`);
      toast.success("Custo deletado com sucesso!");
      fetchCustos();
    } catch (error) {
      toast.error("Erro ao deletar custo");
    }
  };

  const exportarCustosExcel = () => {
    if (custos.length === 0) {
      toast.error("Nenhum custo para exportar");
      return;
    }

    const rows = custos.map(custo => ({
      'Data': formatDate(custo.data),
      'Categoria': custo.categoria,
      'Descrição': custo.descricao,
      'Valor': Number((custo.valor || 0).toFixed(2)),
      'Observações': custo.observacoes || '',
    }));

    exportToExcel(rows, `custos-${new Date().toISOString().split('T')[0]}`, 'Custos');
    toast.success("Custos exportados com sucesso!");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getTotalCustos = () => {
    return custos.reduce((sum, custo) => sum + custo.valor, 0);
  };

  if (loading) return <div>Carregando...</div>;

  return (
    <div className="space-y-6">
      {confirmDialog}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Custos Operacionais</h1>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={exportarCustosExcel} data-testid="exportar-custos-excel-btn">
            <Download className="mr-2 h-4 w-4" />
            Exportar Excel
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => {
                setFormData({
                  data: '',
                  categoria: '',
                  descricao: '',
                  valor: '',
                  observacoes: ''
                });
                setEditingId(null);
              }}>
                <Plus className="mr-2 h-4 w-4" />
                Novo Custo
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingId ? 'Editar Custo' : 'Novo Custo'}</DialogTitle>
                <DialogDescription>
                  Preencha os dados do custo operacional
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="data">Data *</Label>
                    <DatePicker
                      id="data"
                      value={formData.data}
                      onChange={(value) => setFormData({...formData, data: value})}
                      placeholder="Selecione a data"
                    />
                  </div>
                  <div>
                    <Label htmlFor="categoria">Categoria *</Label>
                    <Select
                      value={formData.categoria}
                      onValueChange={(value) => setFormData({...formData, categoria: value})}
                    >
                      <SelectTrigger id="categoria">
                        <SelectValue placeholder="Selecione a categoria" />
                      </SelectTrigger>
                      <SelectContent>
                        {categorias.map((cat) => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="descricao">Descrição *</Label>
                  <Input
                    id="descricao"
                    value={formData.descricao}
                    onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                    placeholder="Ex: Pagamento de INSS do mês"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="valor">Valor (R$) *</Label>
                  <Input
                    id="valor"
                    type="number"
                    step="0.01"
                    value={formData.valor}
                    onChange={(e) => setFormData({...formData, valor: e.target.value})}
                    placeholder="0.00"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="observacoes">Observações</Label>
                  <Textarea
                    id="observacoes"
                    value={formData.observacoes}
                    onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                    placeholder="Informações adicionais (opcional)"
                    rows={3}
                  />
                </div>

                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit">
                    {editingId ? 'Atualizar' : 'Criar'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="dataInicio">Data Início</Label>
              <DatePicker
                id="dataInicio"
                value={filtros.dataInicio}
                onChange={(value) => setFiltros({...filtros, dataInicio: value})}
                placeholder="Data início"
              />
            </div>
            <div>
              <Label htmlFor="dataFim">Data Fim</Label>
              <DatePicker
                id="dataFim"
                value={filtros.dataFim}
                onChange={(value) => setFiltros({...filtros, dataFim: value})}
                placeholder="Data fim"
                minDate={filtros.dataInicio}
              />
            </div>
            <div>
              <Label htmlFor="categoria">Categoria</Label>
              <Select value={filtros.categoria} onValueChange={(value) => setFiltros({...filtros, categoria: value})}>
                <SelectTrigger id="categoria">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas as categorias</SelectItem>
                  {categorias.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => setFiltros({dataInicio: '', dataFim: '', categoria: 'todas'})}
              >
                Limpar Filtros
              </Button>
            </div>
          </div>
          {(filtros.dataInicio || filtros.dataFim || filtros.categoria !== 'todas') && (
            <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
              <p className="text-sm text-amber-900 dark:text-amber-200 font-medium">
                Total filtrado: {formatCurrency(getTotalCustos())} ({custos.length} custos)
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabela de Custos */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Custos</CardTitle>
          <CardDescription>
            Total geral: {formatCurrency(getTotalCustos())} em {custos.length} lançamentos
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Categoria</TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead className="text-right">Valor</TableHead>
                <TableHead>Observações</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {custos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-gray-500 dark:text-gray-400">
                    Nenhum custo encontrado
                  </TableCell>
                </TableRow>
              ) : (
                custos.map((custo) => (
                  <TableRow key={custo.id}>
                    <TableCell>{formatDate(custo.data)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{custo.categoria}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">{custo.descricao}</TableCell>
                    <TableCell className="text-right font-semibold">
                      {formatCurrency(custo.valor)}
                    </TableCell>
                    <TableCell className="text-sm text-gray-600 dark:text-gray-300">
                      {custo.observacoes || '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button size="sm" variant="outline" onClick={() => handleEdit(custo)} data-testid={`editar-custo-${custo.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDelete(custo.id)} data-testid={`excluir-custo-${custo.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Custos;
